import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of, timer } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { UnifiedLoadingCoordinatorService } from '@core/services/infra/unified-loading-coordinator.service';
import { WorkspaceStore } from '../../shared/services/workspace.store';

/**
 * @domain Core/Strategies
 * 
 * Optimized Preloading Strategy - Intelligent and coordinated route preloading
 * 
 * This strategy:
 * - Respects the unified loading coordinator phases
 * - Only preloads essential routes initially
 * - Uses progressive disclosure based on user behavior
 * - Considers network conditions and device capabilities
 * - Prevents resource conflicts during onboarding
 */
@Injectable({
  providedIn: 'root'
})
export class OptimizedPreloadingStrategy implements PreloadingStrategy {
  
  // Essential routes that should be preloaded immediately after auth
  private readonly essentialRoutes = new Set<string>([
    'dashboard',
    'properties'
  ]);

  // High priority routes (preload when ready phase)
  private readonly highPriorityRoutes = new Set<string>([
    'tenants',
    'contracts',
    'properties/add'
  ]);

  // Medium priority routes (preload on demand or after delay)
  private readonly mediumPriorityRoutes = new Set<string>([
    'loans',
    'maintenance',
    'repairs',
    'user-profile',
    'billing-profiles'
  ]);

  // Low priority routes (preload only when idle)
  private readonly lowPriorityRoutes = new Set<string>([
    'admin',
    'help',
    'documentation',
    'contacts',
    'mail',
    'chat'
  ]);

  // Preloading state
  private preloadedRoutes = new Set<string>();
  private isPreloading = false;
  private preloadQueue: { route: Route; load: () => Observable<unknown>; priority: number }[] = [];

  constructor(
    private loadingCoordinator: UnifiedLoadingCoordinatorService,
    private workspaceStore: WorkspaceStore
  ) {}


  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    const routePath = this.getRoutePath(route);

    // Skip if already preloaded
    if (this.preloadedRoutes.has(routePath)) {
      return of(null);
    }

    // Skip auth routes (handled by auth shell)
    if (this.isAuthRoute(route)) {
      return of(null);
    }

    // Tenants only ever use the /tenant/* portal. Don't burn their (often
    // mobile) bandwidth background-preloading the full property-manager app
    // they can never open. Preloading only runs once the workspace is hydrated
    // (ready/complete phases), so this read is reliable and non-blocking.
    if (this.isTenantWorkspace() && !this.isTenantRoute(routePath)) {
      return of(null);
    }

    // Check if we should preload based on current loading phase
    return this.loadingCoordinator.loadingState$.pipe(
      take(1),
      switchMap(state => {
        const canPreload = this.shouldPreloadRoute(routePath, state.currentPhase.name);
        
        if (!canPreload) {
          return of(null);
        }

        // Get route priority
        const priority = this.getRoutePriority(routePath);
        
        // Skip low priority routes on slow connections or during onboarding
        if ((this.isSlowConnection() || state.isOnboardingActive) && priority < 70) {
          return of(null);
        }

        // Add to queue and process
        return this.queueForPreloading(route, load, priority, routePath);
      })
    );
  }

  /**
   * Determine if a route should be preloaded based on current phase
   */
  private shouldPreloadRoute(routePath: string, phase: string): boolean {
    switch (phase) {
      case 'initializing':
      case 'authenticating':
        return false; // No preloading during initial phases
        
      case 'onboarding':
        return false; // No preloading during onboarding to prevent conflicts
        
      case 'preloading':
        return this.essentialRoutes.has(routePath); // Only essential routes
        
      case 'ready':
        return this.essentialRoutes.has(routePath) || 
               this.highPriorityRoutes.has(routePath); // Essential + high priority
        
      case 'complete':
        return true; // All routes can be preloaded when complete
        
      default:
        return false;
    }
  }

  /**
   * Get route priority for preloading order
   */
  private getRoutePriority(routePath: string): number {
    if (this.essentialRoutes.has(routePath)) return 100;
    if (this.highPriorityRoutes.has(routePath)) return 80;
    if (this.mediumPriorityRoutes.has(routePath)) return 60;
    if (this.lowPriorityRoutes.has(routePath)) return 40;
    
    // Default priority for unknown routes
    return 20;
  }

  /**
   * Queue route for preloading
   */
  private queueForPreloading(
    route: Route, 
    load: () => Observable<unknown>,
    priority: number,
    _routePath: string
  ): Observable<unknown> {
    
    // Add to preload queue
    this.preloadQueue.push({ route, load, priority });
    
    // Sort queue by priority (highest first)
    this.preloadQueue.sort((a, b) => b.priority - a.priority);

    // Start preloading if not already in progress
    if (!this.isPreloading) {
      this.startPreloading();
    }

    return of(null);
  }

  /**
   * Start the preloading process with intelligent batching
   */
  private startPreloading(): void {
    if (this.preloadQueue.length === 0) {
      return;
    }

    this.isPreloading = true;
    console.log(`🚀 Starting optimized preloading (${this.preloadQueue.length} routes queued)`);

    // Process queue with delays to prevent overwhelming the browser
    this.processPreloadQueue();
  }

  /**
   * Process preload queue with intelligent timing
   */
  private processPreloadQueue(): void {
    if (this.preloadQueue.length === 0) {
      this.isPreloading = false;
      return;
    }

    // Check if we should continue preloading
    this.loadingCoordinator.loadingState$.pipe(take(1)).subscribe(state => {
      if (!state.currentPhase.allowCacheWarming) {
        this.isPreloading = false;
        return;
      }

      // Get next route to preload
      const next = this.preloadQueue.shift();
      if (!next) {
        this.processPreloadQueue();
        return;
      }

      const routePath = this.getRoutePath(next.route);
      console.log(`🔄 Preloading route: ${routePath} (priority: ${next.priority})`);

      // Preload with timeout
      const _preloadTimeout = timer(5000); // 5 second timeout
      
      next.load().pipe(take(1)).subscribe({
        next: () => {
          this.preloadedRoutes.add(routePath);

          // Continue with next route after a short delay
          timer(this.getPreloadDelay(next.priority)).pipe(take(1)).subscribe(() => {
            this.processPreloadQueue();
          });
        },
        error: (error) => {
          console.warn(`❌ Failed to preload route: ${routePath}`, error);

          // Continue with next route
          timer(100).pipe(take(1)).subscribe(() => {
            this.processPreloadQueue();
          });
        }
      });
    });
  }

  /**
   * Get delay between preloads based on priority
   */
  private getPreloadDelay(priority: number): number {
    if (priority >= 80) return 100;  // High priority: 100ms delay
    if (priority >= 60) return 300;  // Medium priority: 300ms delay
    return 500;                      // Low priority: 500ms delay
  }

  /**
   * Check if connection is slow
   */
  private isSlowConnection(): boolean {
     
    const connection = (navigator as unknown as { connection?: { effectiveType: string; saveData: boolean } }).connection;
    if (!connection) return false;
    
    return connection.effectiveType === 'slow-2g' || 
           connection.effectiveType === '2g' ||
           connection.saveData === true;
  }

  /**
   * Check if route is an auth route
   */
  private isAuthRoute(route: Route): boolean {
    const path = route.path || '';
    return path.includes('auth') ||
           path.includes('login') ||
           path.includes('register') ||
           path.includes('reset-password') ||
           path.includes('verify-email');
  }

  /**
   * Whether the active workspace is a tenant portal. Reads the current
   * workspace signal synchronously; defaults to false (no suppression) if it
   * can't be resolved.
   */
  private isTenantWorkspace(): boolean {
    try {
      return this.workspaceStore.currentWorkspace()?.type === 'tenant';
    } catch {
      return false;
    }
  }

  /** Whether a route belongs to the tenant portal (`/tenant/*`). */
  private isTenantRoute(routePath: string): boolean {
    return routePath === 'tenant' || routePath.startsWith('tenant');
  }

  /**
   * Get clean route path for comparison
   */
  private getRoutePath(route: Route): string {
    let path = route.path || '';
    
    // Remove dynamic segments for comparison
    path = path.replace(/:\w+/g, '');
    path = path.replace(/\/+/g, '/');
    path = path.replace(/^\/|\/$/g, '');
    
    return path || 'root';
  }

  /**
   * Public method to trigger preloading of specific routes
   */
  public preloadSpecificRoutes(routePaths: string[]): void {
    console.log(`🎯 Manually triggering preload for: ${routePaths.join(', ')}`);
    
    // This would need to be implemented based on your routing structure
    // For now, we'll just log the intent
    routePaths.forEach(path => {
      if (!this.preloadedRoutes.has(path)) {
        console.log(`📋 Queued for preload: ${path}`);
      }
    });
  }

  /**
   * Get preloading statistics
   */
  public getPreloadingStats(): { preloaded: number; queued: number; total: number } {
    const totalRoutes = this.essentialRoutes.size + 
                       this.highPriorityRoutes.size + 
                       this.mediumPriorityRoutes.size + 
                       this.lowPriorityRoutes.size;
                       
    return {
      preloaded: this.preloadedRoutes.size,
      queued: this.preloadQueue.length,
      total: totalRoutes
    };
  }
}

